import { useState } from 'react';
import styled from 'styled-components';
import {
  BoldIcon,
  CenterAlignIcon,
  DeleteIcon,
  FontMinusIcon,
  FontPlusIcon,
  ItalicsIcon,
  LeftAlignIcon,
  RightAlignIcon,
} from './optionsIcons';
import OptionsBarColorPicker from './OptionsBarColorPicker';
import LinkEditor from './LinkEditor';
import { DEFAULT_FONT_COLOR } from '@/constants/constants';

const OptionsButtonGroup = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3px 4px;
  background-color: white;
  top: -2rem;
  border: 0.2px solid #c5c7d0;
  filter: drop-shadow(0px 4px 8px rgba(197, 199, 208, 0.25));
  border-radius: 4px;
  box-shadow: 0px 4px 8px rgba(197, 199, 208, 0.25);
  z-index: 5;
  left: 50%;
  transform: translateX(-50%);
`;

const OptionsButton = styled.button`
  cursor: pointer;
  height: 16px;
  width: 18px;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #c5c7d0;
  font-size: 9px;
  color: #676879;
  font-weight: 600;

  &.single {
    border-radius: 3px;
    margin-right: 6px;

    &:last-child {
      margin-right: 0;
    }
  }
  &.group-left {
    border-radius: 3px 0 0 3px;
  }
  &.group-center {
    border-left: none;
    border-right: none;
  }
  &.group-right {
    border-radius: 0 3px 3px 0;
    margin-right: 6px;
  }
  &:hover {
    opacity: 0.8;
    background-color: rgb(247, 248, 249);
  }
  &.active {
    background-color: #0073ea;
    border: 1px solid #0073ea;
  }
  svg {
    height: 10px;
    width: 10px;
  }
`;

const OptionsBar = ({
  item,
  currentSelectedItem,
  addedFormFields,
  updateFormFields,
  removePlaceholder,
}) => {
  const selectedItem = addedFormFields[currentSelectedItem.current];
  const [fontStyle, setFontStyle] = useState(
    selectedItem?.fontStyle
      ? selectedItem.fontStyle
      : {
          bold: false,
          italics: false,
          alignment: 'left',
          fontSize: 11,
          color: DEFAULT_FONT_COLOR,
        },
  );

  const handleFontSettings = (type, color) => {
    const updatedFontStyle = fontStyle;
    let height = 0;
    switch (type) {
      case 'color':
        updatedFontStyle.color = color;
        break;
      case 'bold':
        updatedFontStyle.bold = !updatedFontStyle.bold;
        break;
      case 'italics':
        updatedFontStyle.italics = !updatedFontStyle.italics;
        break;
      case 'align-left':
        updatedFontStyle.alignment = 'left';
        break;
      case 'align-center':
        updatedFontStyle.alignment = 'center';
        break;
      case 'align-right':
        updatedFontStyle.alignment = 'right';
        break;
      case 'font-decrease':
        updatedFontStyle.fontSize -= 1;
        height -= 1;
        break;
      default:
        updatedFontStyle.fontSize += 1;
        height += 1;
        break;
    }
    setFontStyle(updatedFontStyle);
    selectedItem.fontStyle = updatedFontStyle;
    selectedItem.height += height;
    updateFormFields([...addedFormFields]);
  };

  return (
    <OptionsButtonGroup>
      <OptionsButton
        className={fontStyle.bold ? 'active single' : 'single'}
        onClick={() => handleFontSettings('bold')}
      >
        <BoldIcon isActive={fontStyle.bold} />
      </OptionsButton>
      <OptionsButton
        className={fontStyle.italics ? 'active single' : 'single'}
        onClick={() => handleFontSettings('italics')}
      >
        <ItalicsIcon isActive={fontStyle.italics} />
      </OptionsButton>
      <OptionsButton
        className={
          fontStyle.alignment === 'left' ? 'active group-left' : 'group-left'
        }
        onClick={() => handleFontSettings('align-left')}
      >
        <LeftAlignIcon isActive={fontStyle.alignment === 'left'} />
      </OptionsButton>
      <OptionsButton
        className={
          fontStyle.alignment === 'center'
            ? 'active group-center'
            : 'group-center'
        }
        onClick={() => handleFontSettings('align-center')}
      >
        <CenterAlignIcon isActive={fontStyle.alignment === 'center'} />
      </OptionsButton>
      <OptionsButton
        className={
          fontStyle.alignment === 'right' ? 'active group-right' : 'group-right'
        }
        onClick={() => handleFontSettings('align-right')}
      >
        <RightAlignIcon isActive={fontStyle.alignment === 'right'} />
      </OptionsButton>
      <OptionsButton
        className="group-left"
        onClick={() => handleFontSettings('font-decrease')}
      >
        <FontMinusIcon />
      </OptionsButton>
      <OptionsButton className="group-center">
        {fontStyle.fontSize}
      </OptionsButton>
      <OptionsButton
        className="group-right"
        onClick={() => handleFontSettings('font-increase')}
      >
        <FontPlusIcon />
      </OptionsButton>
      <OptionsBarColorPicker
        color={fontStyle.color}
        onClick={(color) => handleFontSettings('color', color)}
      />
      {selectedItem?.urlData ? (
        <LinkEditor
          selectedItem={selectedItem}
          addedFormFields={addedFormFields}
          updateFormFields={updateFormFields}
        />
      ) : null}
      <OptionsButton className="single" onClick={() => removePlaceholder(item)}>
        <DeleteIcon />
      </OptionsButton>
    </OptionsButtonGroup>
  );
};

export default OptionsBar;
